import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, Button, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import ReactMarkdown from 'react-markdown';

import { getArticleBySlug } from '../services/contentApi';
import { Article } from '../types/content';

const ArticleDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let mounted = true;

    getArticleBySlug(slug).then((res) => {
      if (!mounted) return;
      if (res) {
        // If external or PDF, direct open or offer button
        if (res.format === 'external' && res.file_url) {
          window.location.href = res.file_url;
          return;
        }
        setArticle(res);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <main style={{ minHeight: 'calc(100vh - 64px)', paddingTop: 60, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="body1" sx={{ color: '#9CA3AF' }}>Loading note...</Typography>
        </Container>
      </main>
    );
  }

  if (!article) {
    return (
      <main style={{ minHeight: 'calc(100vh - 64px)', paddingTop: 60, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h5" sx={{ color: '#FFFFFF', mb: 2 }}>Note not found</Typography>
          <Button
            component={RouterLink}
            to="/blogs"
            startIcon={<ArrowBackIcon />}
            sx={{ color: '#EFC430' }}
          >
            Back to all notes
          </Button>
        </Container>
      </main>
    );
  }

  return (
    <main
      style={{
        position: 'relative',
        zIndex: 1,
        minHeight: 'calc(100vh - 64px)',
        paddingTop: 40,
        paddingBottom: 80,
      }}
    >
      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
        <Button
          component={RouterLink}
          to="/blogs"
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{
            color: '#9CA3AF',
            textTransform: 'none',
            fontSize: '0.85rem',
            mb: 3,
            '&:hover': { color: '#EFC430' },
          }}
        >
          Back to all notes
        </Button>

        <article
          style={{
            backgroundColor: 'rgba(20, 20, 22, 0.75)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 20,
            padding: '36px 32px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span
              style={{
                color: '#EFC430',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                backgroundColor: 'rgba(239, 196, 48, 0.1)',
                padding: '2px 8px',
                borderRadius: 6,
              }}
            >
              {article.format}
            </span>

            {article.read_time && (
              <span style={{ color: '#9CA3AF', fontSize: '0.8rem' }}>{article.read_time}</span>
            )}
          </div>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.6rem', sm: '2.2rem' },
              color: '#FFFFFF',
              lineHeight: 1.3,
              mb: 2,
            }}
          >
            {article.title}
          </Typography>

          {article.excerpt && (
            <Typography
              variant="body1"
              sx={{ color: '#9CA3AF', fontSize: '1rem', fontStyle: 'italic', mb: 3 }}
            >
              {article.excerpt}
            </Typography>
          )}

          {/* Tags */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {article.tags.map((tag) => (
              <Chip
                key={tag}
                label={`#${tag}`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  color: '#D1D5DB',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </div>

          {/* Body Content */}
          {article.format === 'markdown' && article.content ? (
            <div
              style={{
                color: '#D1D5DB',
                fontSize: '0.98rem',
                lineHeight: 1.8,
              }}
            >
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>
          ) : article.format === 'pdf' && article.file_url ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <Button
                variant="contained"
                endIcon={<LaunchIcon />}
                onClick={() => window.open(article.file_url!, '_blank')}
                sx={{
                  backgroundColor: '#EFC430',
                  color: '#0A0A0A',
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Read PDF Document ↗
              </Button>
            </div>
          ) : null}
        </article>
      </Container>
    </main>
  );
};

export default ArticleDetail;
